/**
 * Marketing completeness (BR-113): the single calculation behind the guide rail, the Marketing step
 * and the Review step. Derived from saved Unit data alone (BR-110): the marketing profile and the
 * unit's approved photos.
 *
 * Six parts, each counted when it holds saved data: description, photos (at least one approved),
 * amenities (at least one), property details, climate and laundry, parking (any field each).
 * Housing restrictions is not counted: an unchecked box cannot be told from an unanswered one.
 * The minimum for the step to count as complete is a description plus one approved photo.
 */

import type { UnitMarketingProfile, UnitPhoto } from '../types/unit-marketing.types';

export const MARKETING_PARTS_TOTAL = 6;

export interface MarketingCompleteness {
  /** round(100 x partsDone / 6), an integer 0..100. */
  percent: number;
  partsDone: number;
  partsTotal: typeof MARKETING_PARTS_TOTAL;
  /** Description plus at least one approved photo. */
  minimumMet: boolean;
  /** Plain-language names of the parts that hold no saved data, in display order. */
  missing: string[];
}

function hasAnyValue(bag: object | null | undefined): boolean {
  if (!bag) return false;
  return Object.values(bag).some((value) => value !== undefined && value !== null && value !== '');
}

export function computeMarketingCompleteness(
  profile: Pick<UnitMarketingProfile, 'description' | 'propertyType' | 'virtualTourUrl' | 'amenities' | 'attributes'> | null,
  photos: readonly Pick<UnitPhoto, 'status'>[]
): MarketingCompleteness {
  const attrs = profile?.attributes ?? null;
  const parts: Array<{ label: string; done: boolean }> = [
    { label: 'Description', done: !!profile?.description?.trim() },
    { label: 'Photos', done: photos.some((photo) => photo.status === 'approved') },
    { label: 'Amenities', done: (profile?.amenities.length ?? 0) > 0 },
    {
      label: 'Property details',
      done:
        !!profile?.propertyType ||
        !!profile?.virtualTourUrl ||
        attrs?.yearBuilt != null ||
        attrs?.floorLevel != null ||
        attrs?.floorCovering != null,
    },
    {
      label: 'Climate and laundry',
      done: !!attrs?.heating || !!attrs?.cooling || !!attrs?.laundry || !!attrs?.heatingFuel,
    },
    { label: 'Parking', done: hasAnyValue(attrs?.parking) },
  ];
  const partsDone = parts.filter((part) => part.done).length;
  return {
    percent: Math.round((100 * partsDone) / MARKETING_PARTS_TOTAL),
    partsDone,
    partsTotal: MARKETING_PARTS_TOTAL,
    minimumMet: parts[0]!.done && parts[1]!.done,
    missing: parts.filter((part) => !part.done).map((part) => part.label),
  };
}

/** "a, b and c" in lower case, for sentences that name the parts still missing. */
export function joinPartNames(names: readonly string[]): string {
  const lower = names.map((name) => name.toLowerCase());
  if (lower.length <= 1) return lower.join('');
  return `${lower.slice(0, -1).join(', ')} and ${lower[lower.length - 1]}`;
}

/**
 * What the minimum still needs, as a phrase for a sentence: "a description and one approved photo",
 * "a description", "one approved photo", or '' once the minimum is met. One wording for the Marketing
 * step and the Review step.
 */
export function missingMinimumPhrase(missing: readonly string[]): string {
  const needs: string[] = [];
  if (missing.includes('Description')) needs.push('a description');
  if (missing.includes('Photos')) needs.push('one approved photo');
  return needs.join(' and ');
}
